import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Elevation from './contents/Elevation';
import { deepCopy, formatUTCDate, isEqualObject, isReverseRoute, parseUTCDate } from '../../utils/FPEUtils';
import CrossSection from 'CrossSection';
import airPlaneOrange from '../../images/contents/crosssection/airplane_orange.png';
import airPlaneAliceBlue from '../../images/contents/crosssection/airplane_alice_blue.png';
import EmphasizeCrossSection from './contents/EmphasizeCrossSection';
import CrossSectionConf from './CrossSectionConf';
import ACOS from './contents/ACOS';
import { useCsColorState } from "../../store/CrossSectionColorState";
import { useATCPlan } from "../../store/ATCPlanState";
import { useFlightPlan } from "../../store/FlightPlanState";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { highlightPointsState } from "../../store/HighlightPointsState";
import {
  elapsedTimeControllerEnable,
  elapsedTimeForWeatherState,
  elapsedTimeState
} from "../../store/ElapsedTimeState";
import { selectedWeatherList, WX_ID_GFS, WX_ID_GSM, WX_ID_LFM, WX_ID_MSM } from '../../store/WeatherContentsState';
import { selectedMapContentsList } from '../../store/MapContentsState';
import { addCsLegendSelector, removeCsLegendSelector } from '../../store/CsLegendState';
import { csRevision } from '../../store/CsRevisionState';
import CsModeChangePanel from './CsModeChangePanel';
import FlightPathModeState from '../../store/FlightPathModeState';
import ToggleValidTime from './ToggleValidTime';
import { csValidTimeVisibleState } from '../../store/CsValidTimeVisibleState';
import { useCsZoomState } from "../../store/CsZoomState";
import WrapOption from "../../constants/WrapOption";
import { CircularProgress, makeStyles } from "@material-ui/core";
import HimawariCldTop from './contents/HimawariCldTop';
import ModelMSM from './contents/ModelMSM';
import ModelLFM from './contents/ModelLFM';
import ModelGSM from './contents/ModelGSM';
import { timeZoneSelector } from "../../store/TimeZoneState";
import { remoteDateState } from '../../store/RemoteDateState';
import { useResizeObserver } from "../../hooks/useResizeObserver";
import { addCsTimeControllerSelector, removeCsTimeControllerSelector } from "../../store/CsTimeControllerState";
import { useLayoutState } from "../../store/LayoutState";
import { csChangeSettingWithId, csGradationState, csSettingState } from '../../store/CsSettingState';
import { coPiPositionDataById, csTargetSelectState, csTargetState } from '../../store/CopilotState';
import { useCrossSectionData } from '../../store/CrossSectionDataState';
import { csAdjustVerticalZoomWithRouteSelector } from '../../store/CsAdjustVerticalZoomWithRouteState';
import CsControlPanel from './CsControlPanel';
import DeferredPromise from "../../utils/DeferredPromise";
import { useCallback } from 'react';
import { useLinkableSourceList } from '../../store/AircraftSettingState';
import clsx from 'clsx';
import ModelGFS from './contents/ModelGFS';

const crossContents = {};
const crossContentsPromise = new DeferredPromise();
let _elapsedTimeForWeather;
let _elapsedTime;
let workerMode = false;
let _csInitialized = false;
let _isGradation = false;
let _adjustVerticalZoomWithRoute;

function FPECrossSection() {
  const routeElapsedTimeEnable = useRecoilValue(elapsedTimeControllerEnable);
  _routeElapsedTimeEnable = routeElapsedTimeEnable;
  return (
    <>
      <Cs/>
      {
        routeElapsedTimeEnable && <CsAirplane />
      }
      <EmphasizeCrossSectionWrapper />
      {
        routeElapsedTimeEnable && <CsModeChangePanel />
      }
      <ToggleValidTime />
      <DrawingProgress />
      <CsControlPanel />
      {_csInitialized && <CsContentsDispatcher />}
    </>
  )
}

const useStyles = makeStyles(() => ({
  progressRoot: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translateY(-50%) translateX(-50%)",
    pointerEvents: "none"
  },
  progress: {
    color: "#1a90ff",
  }
}));

let hiddenProgress;
function DrawingProgress() {

  const classes = useStyles();
  const [hidden, func] = useState(true);
  hiddenProgress = func;

  return (
    <div className={classes.progressRoot} hidden={hidden}>
      <CircularProgress size={30} thickness={5} className={classes.progress} />
    </div>
  );
}

function EmphasizeCrossSectionWrapper() {
  const highlightPoints = useRecoilValue(highlightPointsState);
  const [emphasizeOpt, setEmphasizeOpt] = useState({fixes:[]});

  useEffect( () => {
    let didCancel = false;
    (async()=>{
      const opt = await createEmphasizeOption(highlightPoints);
      if (!didCancel) {
        setEmphasizeOpt(opt);
      }
    })();

    return () => {
      didCancel = true;
    };
  },[highlightPoints]);

  return <EmphasizeCrossSection emphasizeOpt={emphasizeOpt} ></EmphasizeCrossSection>;
}

const yOffset = 7;
const xOffset = 0;

function CsAirplane() {
  const {csColor} = useCsColorState();
  const [airPlane, setAirPlane] = useState(airPlaneOrange);
  const elapsedTime = useRecoilValue(elapsedTimeState);
  const [highlightPoints, setHighlightPoints] = useRecoilState(highlightPointsState);

  useEffect(() => {
    _elapsedTime = elapsedTime;
    updateAirPlaneIcon();
  },[elapsedTime]);

  useEffect(() => {
    if(csColor === "black") {
      setAirPlane(airPlaneAliceBlue);
    } else {
      setAirPlane(airPlaneOrange);
    }
  },[csColor]);

  const sendAirplaneHighlight = (event => {
    let data = [];
    if(event.type === 'mouseover') {
      data.push({
        source: 'crosssection',
        type: 'plane',
        name: 'AirPlane',
        lon: null,
        lat: null,
        index: -1
      });
    }
    setHighlightPoints(data);
  });

  let sonarStyle = "";
  if(highlightPoints.length === 1
    && highlightPoints[0].source !== 'crosssection'
    && highlightPoints[0].type === 'plane') {
    sonarStyle = "cs-airplane-sonar";
  }

  return (
    <div id="airplaneIcon" className={`airplane-cushion-sonar ${sonarStyle}`}>
      <img id="airplaneImage" className="airplane-icon-sonar" src={airPlane} alt="airplaneIcon" onMouseOver={sendAirplaneHighlight} onMouseOut={sendAirplaneHighlight}></img>
    </div>
  );
}

function Cs() {
  const {atcPlan} = useATCPlan();
  const {csLabel, isPlanType, isCoPilotType} = useCrossSectionData();
  const {cs} = useFlightPlan();
  const {csColor} = useCsColorState();
  const setHighlightPoints = useSetRecoilState(highlightPointsState);
  const elapsedTimeForWeather = useRecoilValue(elapsedTimeForWeatherState);
  const highlightPoint = useRef([]);

  const selectedWeather = useRecoilValue(selectedWeatherList);
  const selectedMapContents = useRecoilValue(selectedMapContentsList);
  const addCsLegend = useSetRecoilState(addCsLegendSelector);
  const removeCsLegend = useSetRecoilState(removeCsLegendSelector);
  const addCsTimeController = useSetRecoilState(addCsTimeControllerSelector);
  const removeCsTimeController = useSetRecoilState(removeCsTimeControllerSelector);
  const [revision, setRevision] = useRecoilState(csRevision);
  const flightPathMode = useRecoilValue(FlightPathModeState);
  const csValidTimeVisible = useRecoilValue(csValidTimeVisibleState);
  const {isXZoomEnable, isYZoomEnable} = useCsZoomState();
  const timeZone = useRecoilValue(timeZoneSelector);
  const remoteDate = useRecoilValue(remoteDateState);
  const {isMapOnly} = useLayoutState();
  const csRef = useRef(null);
  const source = useRecoilValue(csTargetState);
  const setCsTargetSelectState = useSetRecoilState(csTargetSelectState);
  const {linkableSourceList} = useLinkableSourceList();
  
  // マウント/アンマウント時に動作する
  useEffect(() => {
    const onMouseOver = touched => {
      if(touched._route_index === undefined || highlightPoint.current === touched._route_index) {
        return;
      }

      highlightPoint.current = touched._route_index

      let type;
      let routeIndex;
      let airwayFixes = [];
      if(!Array.isArray(touched._route_index)) {
        routeIndex = [touched._route_index];
        type = "fix";
      } else {
        routeIndex = touched._route_index;
        type = "route";

        const routeWithAirway = parsedAtcPlan["routeWithAirway"];
        // 選択したルートの終端のFix
        const lastFix = routeWithAirway.find((fix) => {
          return fix[0] === touched._chart._route[routeIndex[routeIndex.length - 1]][0];
        });
        // 選択したルートの先端のFIXと終端のFIXがAirwayでありかつ同一であるか
        const isMatchedAirway = routeWithAirway.some((fix) => {
          return fix[0] === touched._chart._route[routeIndex[0]][0] && fix[3] && fix[3] === lastFix[3] && fix[4] === lastFix[4];
        });

        if(isMatchedAirway) {
          type = "airway";

          // 該当のairwayを持つFIXに絞る
          for(let i = 0; i < routeWithAirway.length; i++) {
            const fix = routeWithAirway[i];
            if(fix[3] === lastFix[3] && fix[4] === lastFix[4]) {
              airwayFixes.push({name: fix[0], lon: fix[2], lat: fix[1], source: 'crosssection', type: type});
            }
          }
        }
      }

      let data = [];
      if(type === "airway") {
        data = airwayFixes;
      } else {
        routeIndex.forEach((idx) => {
          const fix = touched._chart._route[idx];
          data.push({name: fix[0], lon: fix[2], lat: fix[1], index: idx, source: 'crosssection', type: type});
        });
      } 
  
      setHighlightPoints(data);
    }
    const onMouseOut = () => {
      if(highlightPoint.current === null) {
        return;
      }
      highlightPoint.current = null;
      setHighlightPoints([]);
    }
    initCrossSection(onMouseOver, onMouseOut, setRevision, remoteDate);
  },[setHighlightPoints, setRevision, remoteDate]);

  // 気象コンテンツのON/OFF時に動作する
  useEffect(() => {
    const setContentsState = (weatherState, mapContentsState) => {
      Object.keys(crossContents).forEach(key => {
        const visible = weatherState.includes(key) || mapContentsState.includes(key);
        const contents = crossContents[key];
        if(visible && !contents.isVisible() && contents.getLegend) {
          addCsLegend(contents.getLegend());
        } else if(!visible && contents.isVisible() && contents.getLegend){
          removeCsLegend(contents.getLegend());
        }
        if (typeof contents.getTimeControllerInfo === 'function') {
          if (visible && !contents.isVisible()) {
            addCsTimeController(contents.getTimeControllerInfo());
          } else if (!visible && contents.isVisible()) {
            removeCsTimeController(contents.getTimeControllerInfo());
          }
        }
        if (visible !== contents.isVisible()) {
          contents.setVisible(visible);
        }
      });
    }
    setContentsState(selectedWeather, selectedMapContents);
    updateCrossSection();
  },[selectedWeather, selectedMapContents, addCsLegend, removeCsLegend, addCsTimeController, removeCsTimeController]);

  useEffect(() => {
    updateCrossSection();
  },[revision]);

  useEffect(() => {
    _fpMode = flightPathMode;
    if(!flightPathMode) {
      csController.enableTimeLabel(false);
    } else {
      csController.enableTimeLabel(csValidTimeVisible);
    }
    updateCrossSection();
  },[flightPathMode, csValidTimeVisible]);

  useEffect(() => {
    csController.enableXZoom(isXZoomEnable);
  },[isXZoomEnable]);

  useEffect(() => {
    csController.enableYZoom(isYZoomEnable);
  },[isYZoomEnable]);

  // TimeZone変更時に動作する
  useEffect(() => {
    csController.setTimeZone(timeZone);
  },[timeZone]);

  // atcPlanのjson変更時に動作する
  useEffect(() => {
    parseAtcPlan(atcPlan.edit.json);
    updateCrossSection();
  },[atcPlan.edit.json]);

  // 飛行ルート（飛行計画）変更時に動作する
  useEffect(() => {
    if (isPlanType && cs) {
      parseFlightPlan(cs);
      updateCrossSection();
    }
  },[isPlanType, cs]);

  // 飛行ルート（CoPilot航跡）変更時に動作する
  const csTarget = useRecoilValue(csTargetState);
  const data = useRecoilValue(coPiPositionDataById(csTarget.id));
  useEffect(() => {
    if (isCoPilotType) {
      let target;
      if (data) {
        target = {...data, ...{data: data.data.filter(d => d.properties.data_source_id === csTarget.dataSourceId)}};
      }
      perseCoPiData(target);
      updateCrossSection();
    }
  }, [data, isCoPilotType, csTarget]);

  //飛行計画, CoPilot航跡どちらもOFF、かつATCPlanの入力もない場合、断面図描画をクリアする
  useEffect(() => {
    if (!isPlanType && !isCoPilotType && Object.keys(atcPlan.edit.json).length === 0) {
      etd = undefined;
      parsedAtcPlan = {};
      updateCrossSection();
    }
  }, [isPlanType, isCoPilotType, atcPlan.edit.json]);

  // タイムスライダーで時制変更時に動作する
  useEffect(() => {
    _elapsedTimeForWeather = elapsedTimeForWeather;
    if(!_fpMode) {
      updateCrossSection();
    } else {
      updateAirPlaneIcon();
    }
    
  },[elapsedTimeForWeather]);

  // 断面図のサイズ変更時に動作する
  useResizeObserver(csRef, () => {
    resizeCrossSection();
  });

  useEffect(() => {
    setCrossSectionColor(csColor);
  },[csColor]);

  // レイアウトで地図Onlyに切り替えたときに動作する（true: updateCrossSectionをスルーするようになる）
  useLayoutEffect(() => {
    _hidden = isMapOnly;
    if (!_hidden) {
      updateCrossSection();
    }
  },[isMapOnly]);

  const labelClickHandler = useCallback(someSources => {
    if (someSources) {
      setCsTargetSelectState(source.id);
    }
  }, [source, setCsTargetSelectState]);

  return (
    <>
      <div id="cross-section" className="cross-section" ref={csRef}>
        <div style={{position: 'relative', width:'100%', height:'100%', padding:0, margin:0, fontSize:'14px'}}>
          <canvas id={"cross_section_chart"}></canvas>
        </div>
      </div>
      {
        csLabel !== "" && <div className="cross-section-label">
          <div>{csLabel}</div>
          {
            isCoPilotType && <div onClick={() => labelClickHandler(source.someSources)} className={clsx("cross-section-source-label", source.someSources && "cross-section-source-label-cursor")}>
              {linkableSourceList.find(l => l.data_source_id === source.dataSourceId)?.data_source_name}
            </div>
          }
        </div>
      }
    </>
  );

}

let parsedAtcPlan = {};
let etd;
const parseAtcPlan = (json) => {

  if(Object.keys(json).length === 0) {
    parsedAtcPlan = {};
    return;
  }

  // ルート
  const route = [];
  const routeWithAirway = [];
  // dep
  const dep = json.dep;
  route.push([dep.icao, dep.lat, dep.lon, 0, null, 0]);
  // 間のFIX
  let cumulativeTime = createRoute(json.route, route, 0, routeWithAirway);
  // arr
  const arr = json.arr;
  cumulativeTime += arr.time;
  const tmpLastFix = route[route.length -1];
  if(arr.icao === tmpLastFix[0] && arr.lat === tmpLastFix[1] && arr.lon === tmpLastFix[2]) {
    route.pop();
  }
  route.push([arr.icao, arr.lat, arr.lon, 0, null, cumulativeTime]);
  parsedAtcPlan["MainRoute"] = route;
  parsedAtcPlan["MainRouteType"] = "plan";
  parsedAtcPlan["routeWithAirway"] = routeWithAirway;

  // ETD
  if(json.dof) {
    etd = parseUTCDate(`${json.dof} ${dep.etd}00`);
  } else {
    etd = new Date();
    const depTime = parseInt(dep.etd.substr(0, 2));
    const depMin = parseInt(dep.etd.substr(2));
    if(depTime < etd.getUTCHours() || (depTime === etd.getUTCHours() && depMin < etd.getUTCMinutes())) {
      etd.setUTCDate(etd.getUTCDate() + 1);
    }
    etd.setUTCHours(depTime, depMin, 0);
  }
  
  const formattedEtd = formatUTCDate(etd);
  parsedAtcPlan["ETD"] = formattedEtd;

  // ETA
  parsedAtcPlan["ETA"] = formatUTCDate(new Date(etd.getTime() + cumulativeTime * 1000));;

  calcDistAndFl(route);

  setIconStyle(dep.lon, arr.lon);
}

const createRoute = (data, route, cumulativeTime = 0, routeWithAirway, airwayName, counter = 0) => {
  for(let i = 0; i < data.length; i++) {
    const record = data[i];
    if(record.hasOwnProperty('airway')) {
      cumulativeTime = createRoute(record.airway, route, cumulativeTime, routeWithAirway, record.airwayName, counter++);
    } else if (route[route.length - 1][0] !== record.name) {
      cumulativeTime += record.time;
      route.push([record.name, record.lat, record.lon, record.level, null, cumulativeTime]);
      routeWithAirway.push([record.name, record.lat, record.lon, airwayName, counter]);
    } else if(airwayName){
      routeWithAirway.push([record.name, record.lat, record.lon, airwayName, counter]);
    }
  }
  return cumulativeTime;
}

const parseFlightPlan = (flightPlan) => {
  etd = undefined;
  if(Object.keys(flightPlan).length === 0) {
    parsedAtcPlan = {};
    return;
  }

  // 出発時刻
  if (flightPlan.etd) {
    etd = parseUTCDate(flightPlan.etd);
    parsedAtcPlan["ETD"] = formatUTCDate(etd);
  }

  // 到着時刻
  if (flightPlan.eta || flightPlan.sta) {
    const eta = parseUTCDate(flightPlan.eta || flightPlan.sta);
    parsedAtcPlan["ETA"] = formatUTCDate(eta);
  }

  // ルート
  const route = [];
  const routeWithAirway = [];
  let counter = 0;
  let prevTime;
  flightPlan.routeList.forEach((p, pIdx, pList) => {
    if (!p.fixes) {
      const calced = calcCumulativeTime(p.transitTime, prevTime, etd);
      route.push([p.name, p.coordinate[1], p.coordinate[0], p.alt, null, calced.cumulativeTime]);
      if (calced.time) {
        prevTime = calced.time;
      }
      routeWithAirway.push([p.name, p.coordinate[1], p.coordinate[0], undefined, counter]);
      counter++;
    } else {
      p.fixes.forEach((ap, apIdx, apList) => {
        if (apIdx !== 0 && apIdx !== apList.length - 1) {
          const calced = calcCumulativeTime(ap.transitTime, prevTime, etd);
          route.push([ap.name, ap.coordinate[1], ap.coordinate[0], ap.alt, null, calced.cumulativeTime]);
          if (calced.time) {
            prevTime = calced.time;
          }
        }
        routeWithAirway.push([ap.name, ap.coordinate[1], ap.coordinate[0], p.name, counter]);
      });
      counter++;
    }
  });
  parsedAtcPlan["MainRoute"] = route;
  parsedAtcPlan["MainRouteType"] = "plan";
  parsedAtcPlan["routeWithAirway"] = routeWithAirway;

  calcDistAndFl(route);

  let prev;
  route.forEach((p, pIdx, list) => {
    if (prev && p[5] === undefined) {
      let nextIndex = list.slice(pIdx + 1).findIndex(next => next[5] !== undefined);
      nextIndex = nextIndex !== undefined ? nextIndex + pIdx + 1 : pIdx + 1;
      const baseTime = prev[5];
      const baseDist = prev[4];
      const rangeTime = list[nextIndex][5] - baseTime;
      const rangeDist = list[nextIndex][4] - baseDist;
      list.forEach((pp, ppIdx) => {
        if (ppIdx >= pIdx && ppIdx < nextIndex) {
          pp[5] = baseTime + ((pp[4] - baseDist) / rangeDist * rangeTime);
        }
      });
    } else {
      prev = p;
    }
  });

  setIconStyle(route[0][2], route[route.length-1][2]);
}

const perseCoPiData = (coPiData) => {
  if (!coPiData || !coPiData.data || coPiData.data.length === 0) {
    parsedAtcPlan = {};
    return;
  }

  let points = coPiData.data.filter(d => d.geometry.type === 'Point');
  points = points.sort((a, b) => {
    return a.properties.obs_time < b.properties.obs_time ? -1 : 1;
  });

  parsedAtcPlan = {};
  parsedAtcPlan['ETD'] = points[0].properties.obs_time;
  parsedAtcPlan['ETA'] = points[points.length - 1].properties.obs_time;
  parsedAtcPlan['MainRouteType'] = 'plan';
  parsedAtcPlan["routeWithAirway"] = [];

  const route = [];
  let time = 0;
  points.forEach((data, idx) => {
    const coordinate = data.geometry.coordinates;
    
    if (idx !== 0) {
      // 時間計算
      const currentDate = new Date(data.properties.obs_time);
      const prevData = points[idx - 1];
      const prevDate = new Date(prevData.properties.obs_time);
      time += (currentDate.getTime() - prevDate.getTime()) / 1000;
    }

    // 地点名, lat, lon, altitude, null(未使用), 積算の経過時間
    const addData = [data.properties.name, coordinate[1], coordinate[0], points[idx].properties.alt_ft, null, time];
    route.push(addData);
  });
  calcDistAndFl(route);
  parsedAtcPlan['MainRoute'] = route;
}

const calcCumulativeTime = (time, prevTime, etd) => {
  let cumulativeTime;
  let d;
  if (typeof time === "string" && time.length === 4 && etd !== undefined) {
    d = prevTime ? new Date(prevTime.getTime()) : etd ? new Date(etd.getTime()) : new Date();
    d.setHours(time.substr(0, 2));
    d.setMinutes(time.substr(2));
    d.setSeconds(0);
    d.setMilliseconds(0);
    if (prevTime && prevTime.getTime() > d.getTime()) {
      d = new Date(d.getTime() + 24*60*60*1000);
    }
    cumulativeTime = (d.getTime() - etd.getTime()) / 1000;
  } else if (typeof time === "number" && etd !== undefined) {
    d = new Date(time);
    cumulativeTime = (d.getTime() - (etd ? etd.getTime() : new Date().getTime())) / 1000;
  }
  return {cumulativeTime, time: d};
}

const setIconStyle = (depLon, arrLon) => {
  const iconDom = document.getElementById('airplaneImage');
  if (!iconDom) {
    return;
  }
  if(isReverseRoute(depLon, arrLon)) {
    iconDom.style.transform = 'scaleX(-1)';
  } else {
    iconDom.style.transform = null;
  }
}

/**
 * 断面図をリサイズする
 */
const resizeCrossSection = () => {
  csController.resize();
  updateAirPlaneIcon();
}

/**
 * CrossSection.Controller
 */
let csController;
/**
 * 断面図左ルーラーの幅
 */
let leftRulerWidth;
/**
 * 断面図右ルーラーの幅
 */
let rightRulerWidth;
/**
 * 断面図下部マージン
 */
 let bottomMargin;
 /**
  * 断面図上部マージン
  */
 let topMargin;
 /**
 * 強調線の幅
 */
let highlightStrokeWidth;
 
/**
 * 断面図の初期化処理
 */
const initCrossSection = async (onMouseOver, onMouseOut, setRevision, remoteDate) => {
  // crossContents[ModelUKMET.NAME] = new ModelUKMET(setRevision);
  // crossContents[SatCld.NAME] = new SatCld(setRevision);
  crossContents[HimawariCldTop.NAME] = new HimawariCldTop(setRevision);
  // crossContents[ASCScale.NAME] = new ASCScale(setRevision);
  crossContents[ACOS.NAME] = new ACOS(setRevision, switchAutoUpdateInterval);
  crossContents[Elevation.NAME] = new Elevation(setRevision);
  crossContents[ModelMSM.NAME] = new ModelMSM(setRevision);
  crossContents[ModelLFM.NAME] = new ModelLFM(setRevision);
  crossContents[ModelGSM.NAME] = new ModelGSM(setRevision);
  crossContents[ModelGFS.NAME] = new ModelGFS(setRevision);

  const canvas = document.getElementById('cross_section_chart');

  if (window.Worker && canvas.transferControlToOffscreen && window.self.crossOriginIsolated) {
    //OffscreenCanvasに対応しているのでWeb Workerを使い別スレッドで断面図を描画する
    workerMode = true;
    const worker = new Worker(`cs-worker-${process.env.REACT_APP_CS_WORKER_VERSION}.js`);
    //Workerスレッド内の時刻補正をする
    worker.postMessage({type: 'syncDate', payload: {lag: remoteDate.getLag()}});
    const wop = WrapOption();
    const wrapOption = {
      conf_path: "pri/conf/data",
      ascscale_img_path: null,
      option: {
        wrap_url: wop.wrap_url,
        expo_map_url: wop.expo_map_url,
        expo_tile_sat_url: wop.expo_tile_sat_url,
        expo_tile_gpv_url: wop.expo_tile_gpv_url,
        numerical_db_gpv_url: wop.numerical_db_gpv_url
      }
    };
    csController = new CrossSection.CrossSectionControllerProxy(worker, canvas, wrapOption);
  } else {
    //従来のシングルスレッドで断面図を描画
    csController = new CrossSection.Controller('cross-section');
  }
  
  csController.addEventListenerOnMouseOver(onMouseOver);
  csController.addEventListenerOnMouseOut(onMouseOut);
  const config = CrossSectionConf;
  leftRulerWidth = config.left_ruler_width ? config.left_ruler_width : 40.0;
  rightRulerWidth = config.right_ruler_width ? config.right_ruler_width : 40.0;
  bottomMargin = config.bottom_ruler_height ? config.bottom_ruler_height : 50.0;
  topMargin = config.top_margin ? config.top_margin : 60.0;
  topMargin -= 5.0; // 断面図のコンテンツがtop_marginより少し上まではみ出して描画されるため調整
  highlightStrokeWidth = config.plan_route_line_width ? config.plan_route_line_width : 1;
  csController.initAppearance(config);
  csController.boundsChanged(boundsChange);
  _csInitialized = true;

  crossContentsPromise.resolve();
}

/**
 * 断面図モジュールにコンテンツを設定する
 */
const setObjects = async () => {
  for await (let contentsName of Object.keys(crossContents)) {
    let contents = crossContents[contentsName];
    if(!contents.getDataHandler() && contents.isVisible()) {
      await contents.addObject();
      let names = contents.getDataName();
      let references = contents.getDataReference();
      let handlers = contents.getDataHandler();
      if (!Array.isArray(names)) {
        names = [names];
        references = [references];
        handlers = [handlers];
      }

      let index = 0;
      for (const name of names) {
        if (name.match(/^TERRAIN_ELEVATION$/i)) {
          await csController.setMaximumTerrainElevation(references[index]);
        } else {
          if (workerMode) {
            const conf = deepCopy(references[index]._data.conf);
            if (!name.match(/^ACOS$/i)) {
              if(conf.DataHandler) {
                // DH1
                conf.Attributes.UpdateInterval = 999999;
              } else {
                // DH2
                conf.Source.timelist.update_interval = 999999;
              }
            }
            await csController.setObject(name, conf);
          } else {
            await csController.setObject(name, handlers[index]);
          }
        }
        index++;
      }
    } 
  }
}

/**
 * 一つ前に表示していた断面図に設定したオプション
 */
let cacheOpt;

/**
 * ルートIDに設定するカウンター
 */
let routeIdCount = 0;
let _fpMode = false;
let _hidden = false;
let _routeElapsedTimeEnable = false;
/**
 * 断面図を更新する
 */
export const updateCrossSection = async () => {
  //レイアウトが地図Onlyの場合、描画処理を実行しない
  if (_hidden) {
    return
  }
  await setObjects();
  const opt = await getOpt(_fpMode, _routeElapsedTimeEnable);
  const optJson = sortObjectAndConvertToJSON(opt);
  const cacheOptJson = cacheOpt ? sortObjectAndConvertToJSON(cacheOpt) : null;
  if(cacheOptJson !== optJson && Object.keys(opt).length) {
    const routeId = getRouteID(opt);
    cacheOpt = deepCopy(opt);
    opt["RouteID"] = routeId;
    if(opt["MainRoute"]) {
      hiddenProgress(false);
    }
    opt['routeAltitudeGradation'] = _isGradation;
    return new Promise(resolve => {
      // console.log(JSON.stringify(opt, null, '\t'))
      csController.update(opt, () => {
        updateComplete();
        resolve();
      }, updateErr);
    });
  } else {
    updateAirPlaneIcon();
  }
}

/**
 * 断面図に渡すオプションのオブジェクトをソートしてJsonに変換する
 * @param {Object} opt 断面図モジュールに渡すオプション
 * @returns String
 */
const sortObjectAndConvertToJSON = (opt) => {
  const sorted = Object.entries(opt).sort();
  for(let i in sorted){
    const val = sorted[i][1];
    if(!Array.isArray(val) && typeof val === "object"){
        sorted[i][1] = sortObjectAndConvertToJSON(val);
    }
  }
  return JSON.stringify(sorted);
}

/**
 * 断面図に渡すオプションRouteIDを返却する。
 * ルートに変更がある場合は新しいRouteIDを払い出す。
 * @param {Object} opt 断面図モジュールに渡すオプション
 * @returns String
 */
const getRouteID = (opt) => {
  if (cacheOpt && opt 
    && isEqualObject(cacheOpt.ETD, opt.ETD)
    && isEqualObject(cacheOpt.ETA, opt.ETA)
    && isEqualObject(cacheOpt.MainRoute, opt.MainRoute)
    && isEqualObject(cacheOpt.MainRouteType, opt.MainRouteType)
    && isEqualObject(cacheOpt.adjustVerticalZoomWithRoute, opt.adjustVerticalZoomWithRoute)) {
    return routeIdCount;
  }
  return ++routeIdCount;
}

/**
 * 描画完了時のCallback関数
 */
const updateComplete = () => {
  console.log("crossSection draw Complete");
  _adjustVerticalZoomWithRoute = null;
  updateAirPlaneIcon();
  for(const key of Object.keys(crossContents)) {
    crossContents[key].afterDraw();
  }
  hiddenProgress(true);
}

/**
 * 飛行機アイコンをの位置を設定する
 */
const updateAirPlaneIcon = async () => {
  const iconDom = document.getElementById('airplaneIcon');
  if(!iconDom) {
    return;
  }
  if(!parsedAtcPlan.MainRoute || parsedAtcPlan.MainRoute.length === 0) {
    iconDom.style.display = 'none';
    return;
  }
  let left;
  let top;

  const alt = calcAltitudeBySec(_elapsedTime);
  if (workerMode) {
    // Web Workerとのやり取りの回数削減のためXY軸座標を同時に取得
    [left, top] = await csController.getXYPosition(_elapsedTime, alt / 100.0);
    left -= xOffset;
    top -= yOffset;
  } else {
    // X軸座標取得
    left = csController.getXTimePosition(_elapsedTime) - xOffset;
    // Y軸座標取得
    top = csController.getYPosition(alt / 100.0) - yOffset;
  }

  const cs = document.getElementById('cross-section');
  if(left + xOffset < leftRulerWidth || cs.clientWidth - rightRulerWidth - xOffset < left) {
    iconDom.style.display = 'none';
  } else if(top + yOffset < topMargin || cs.clientHeight - bottomMargin - yOffset < top) {
    iconDom.style.display = 'none';
  } else {
    iconDom.style.display = 'block';
  }
  iconDom.style.left = `${left}px`;
  iconDom.style.top = `${top}px`;
}

const calcAltitudeBySec = paramSec => {

  let prevData;
  for(let i = 0; i < parsedAtcPlan.MainRoute.length; i++) {
    const data = parsedAtcPlan.MainRoute[i];
    const dataSec = data[5];
    if(dataSec === paramSec) {
      return data[3];
    } else if(dataSec < paramSec && i !== parsedAtcPlan.MainRoute.length -1) {
      prevData = data;
    } else {
      const proportion = (paramSec - prevData[5]) / (data[5] - prevData[5]);
      let alt = prevData[3] + (data[3] - prevData[3]) * proportion;
      if(alt < 0) {
        alt = 0;
      }
      return alt;
    }
  }
}

const calcDistAndFl = (route) => {
  const routeLength = route.length;

  let prevData;
  let totalDist = 0;
  for(const currentData of route) {
    if(!prevData) {
      currentData[4] = totalDist;
      prevData = currentData;
      continue;
    }
    const dist = CrossSection.Util.routeDistance(prevData[1], prevData[2], currentData[1], currentData[2]);
    currentData[4] = (totalDist += dist);
    prevData = currentData;
  }

  for(let i = 1; i < routeLength; i++) {
    let fl = route[i][3];
    if ( fl <= 0 ) {
      let pd = 0;
      let pl = 0;
      for ( let j = i-1 ; j >= 0 ; j-- ) {
        if ( route[j][3] !== 0 ) {
          pd = parseFloat(route[j][4]);
          pl = parseFloat(route[j][3]);
          break;
        }
      }
      let nd = parseFloat(route[routeLength-1][4]);
      let nl = parseFloat(route[routeLength-1][3]);
      for ( let j = i+1 ; j < routeLength ; j++ ) {
        if ( route[j][3] !== 0 ) {
          nd = parseFloat(route[j][4]);
          nl = parseFloat(route[j][3]);
          break;
        }
      }
      fl = CrossSection.Util.interpolate(pd, nd, route[i][4], pl, nl);
      route[i][3] = fl;
    }
  }
}

/**
 * 描画失敗時のCallback関数
 */
const updateErr = err => {
  console.log("crossSection draw Error");
  console.log(err);
  hiddenProgress(true);
}

/**
 * 描画しているコンテンツの画角変更処理を実装する
 */
const boundsChange = () => {
  updateAirPlaneIcon();
}

/**
 * 断面図描画オプションを取得する
 */
let _color = "black";
const getOpt = async (fpMode, routeElapsedTimeEnable) => {
  const opt = getRoute();
  opt["Color"] = _color;
  let validTime;
  let _now;
  if (routeElapsedTimeEnable && etd) {
    //ルート経過時間タイムスライダーが有効な場合
    //ETD + elapsed
    validTime = formatUTCDate(new Date(etd.getTime() + _elapsedTimeForWeather * 1000));
  } else {
    _now = formatUTCDate(new Date());
  }
  for await (let contentsName of Object.keys(crossContents)) {
    let contents = crossContents[contentsName];
    if(contents.isVisible() && contents.isReady()) {

      if (!routeElapsedTimeEnable) {
        // ルート経過時間タイムスライダーが無効な場合
        if (typeof contents.getTimeControllerInfo === 'function') {
          //タイムコントローラが設定されているコンテンツであれば、自動更新中は現在時刻、そうでなければcurrentTime
          const timeController = contents.getTimeControllerInfo().timeController;
          validTime = timeController.autoRefresh ? _now : timeController.currentTime;
        } else {
          //タイムコントローラが設定されていないコンテンツは現在時刻
          validTime = _now;
        }
      }

      Object.assign(opt, await contents.getOpt(validTime, fpMode));
    }
  }
  if (_adjustVerticalZoomWithRoute) {
    opt.adjustVerticalZoomWithRoute = _adjustVerticalZoomWithRoute;
  }
  return opt;
}

/**
 * ルート情報を取得する
 */
const getRoute = () => {
  return JSON.parse(JSON.stringify(parsedAtcPlan));
}

const setCrossSectionColor = color => {
  _color = color;
  updateCrossSection()
}

const makeFixOption = async (point, type) => {
  const cs = document.getElementById('cross-section');
  const rightLimit = cs.clientWidth - rightRulerWidth;
  const bottomLimit = cs.clientHeight - bottomMargin;
  let top, left;
  if (workerMode) {
    // if (point[5] !== undefined) {
    //   [left, top] = await csController.getXYPosition(point[5], point[3] / 100.0);
    // } else {
      [left, top] = await csController.getXYPosition(point[4], point[3] / 100.0, false);
    // }
  } else {
    top = csController.getYPosition(point[3] / 100.0);
    // if (point[5] !== undefined) {
    //   left = csController.getXTimePosition(point[5]);
    // } else {
      left = csController.getXDistancePosition(point[4], true);
    // }
  }

  let fixVisible = true;
  if(left < leftRulerWidth || rightLimit < left) {
    fixVisible = false;
  } else if(top < topMargin || bottomLimit < top) {
    fixVisible = false;
  }
  return {top: top, left: left, fixVisible:fixVisible, type: type};
};

const createEmphasizeOption = async (highlightPoints) => {

  if (!parsedAtcPlan.MainRoute) {
    return {fixes: []};
  }

  const fixes = [];
  if (highlightPoints && highlightPoints.length > 0) {
    if(highlightPoints[0].source === "crosssection") {
      return {fixes: []};
    }
    if (highlightPoints[0].type === 'plane') {
      return {fixes: []};
    }
    if (highlightPoints.length === 1) {
      const filtered = parsedAtcPlan.MainRoute.filter(p => p[0] === highlightPoints[0].name && p[1] === highlightPoints[0].lat && p[2] === highlightPoints[0].lon);
      for (let i=0; i<filtered.length; i++) {
        const p = filtered[i];
        const opt = await makeFixOption(p, highlightPoints[0].type);
        fixes.push(opt);
      }
    } else {
      for (let i=0; i<parsedAtcPlan.MainRoute.length; i++) {
        const current = parsedAtcPlan.MainRoute[i];
        const prev = i > 0 ? parsedAtcPlan.MainRoute[i - 1] : undefined;
        const next = i < parsedAtcPlan.MainRoute.length - 1 ? parsedAtcPlan.MainRoute[i + 1] : undefined;
        const targets = [prev, current, next].filter(v=>v);
        const filtered = targets.filter(p => {
          return highlightPoints.filter(point => p[0] === point.name && p[1] === point.lat && p[2] === point.lon).length > 0
        });
        if(filtered.length > 1 && filtered.includes(current)) {
          const opt = await makeFixOption(current, filtered[0].type);
          fixes.push(opt);
        }
      }
    }
  }
  return {
    fixes: fixes,
    rulerWidth: {
      left: leftRulerWidth,
      right: rightRulerWidth,
      top: topMargin,
      bottom: bottomMargin
    },
    strokeWidth: highlightStrokeWidth
  };
}

const switchAutoUpdateInterval = target => {
  if (!workerMode) return;
  return new Promise(resolve => {
    Object.keys(crossContents).forEach(k => {
      if (0 < k.indexOf(target)) {
        const contents = crossContents[k];
        csController.switchAutoUpdateInterval(contents.getDataName(), contents.autoRefresh && contents.visible).then(ret => {
          resolve(ret);
        });
      }
    });
  });
}

export const getCrossContentsByPresetId = async (id) => {
  if (crossContentsPromise.state === 'pending') {
    //initCrossSectionの完了を待ち受ける
    await crossContentsPromise.promise;
  }
  let key;
  Object.keys(crossContents).forEach(k => {
    const contents = crossContents[k];
    if (contents.presetId === id) {
      key = k;
    }
  });
  return crossContents[key];
};

function CsContentsDispatcher() {
  //ACOS断面図でFT ALL表示すると大量リクエストが送信されエラーが発生する（ACOSが表示されない）ため、ACOS断面図でFT ALL表示はomit
  // const acosSetting = useRecoilValue(csChangeSettingWithId(ACOS.CS_NAME));
  // useEffect(() => {
  //   crossContents[WX_ID_ACOS].setContentsSettings(acosSetting);
  // }, [acosSetting]);

  const lfmSetting = useRecoilValue(csChangeSettingWithId(ModelLFM.CS_NAME));
  useEffect(() => {
    crossContents[WX_ID_LFM].setContentsSettings(lfmSetting);
  }, [lfmSetting]);

  const msmSetting = useRecoilValue(csChangeSettingWithId(ModelMSM.CS_NAME));
  useEffect(() => {
    crossContents[WX_ID_MSM].setContentsSettings(msmSetting);
  }, [msmSetting]);

  const gsmSetting = useRecoilValue(csChangeSettingWithId(ModelGSM.CS_NAME));
  useEffect(() => {
    crossContents[WX_ID_GSM].setContentsSettings(gsmSetting);
  }, [gsmSetting]);

  const gfsSetting = useRecoilValue(csChangeSettingWithId(ModelGFS.CS_NAME));
  useEffect(() => {
    crossContents[WX_ID_GFS].setContentsSettings(gfsSetting);
  }, [gfsSetting]);

  const elevationSetting = useRecoilValue(csChangeSettingWithId(Elevation.CS_NAME));
  useEffect(() => {
    crossContents[Elevation.NAME].setContentsSettings(elevationSetting)
  }, [elevationSetting]);

  const allSettings = useRecoilValue(csSettingState);
  useEffect(() => {
    updateCrossSection();
  }, [allSettings])

  const csGradation = useRecoilValue(csGradationState);
  useEffect(() => {
    _isGradation = csGradation;
  }, [csGradation]);

  const csAdjustVerticalZoomWithRoute = useRecoilValue(csAdjustVerticalZoomWithRouteSelector);
  useEffect(() => {
    _adjustVerticalZoomWithRoute = csAdjustVerticalZoomWithRoute;
    if (csAdjustVerticalZoomWithRoute.update) {
      updateCrossSection();
    }
  }, [csAdjustVerticalZoomWithRoute])

  return <></>
}

export default FPECrossSection;